/* 추론 1(풀이)의 출력 계약. 풀이는 글이 먼저고, 이 JSON 은 그 글에서 값을 옮긴 것이다: 문항의 기호에 정한 값, 그림 유무, 답.
 * 그림의 개체(곡선, 점, 접선, 교점, 측정)는 풀이가 계산한 값을 찍는 것이 아니라 색칠도구 호출(입력 단계, 연계 단계)로 mathcore 가 세운다.
 * 풀이 글의 $…$ 식은 제시된 식에 더해진다 */
import { givenFormulas, pointNamesIn } from './segments';
import { readCurveShape, readExpressionShape, unwrapMath } from './expr';
import { readValues, visibleNotation } from './symbols';
import { num } from './measure';
import { NO_TARGET, TARGET_SCHEMA, readTarget } from './target';
import type { Target } from './target';

export type Figure = '있음' | '없음';

export interface Solved {
  /** 문항에 그릴 그림이 있는가. 「없음」이면 생성 단계를 돌리지 않는다. 모델이 찍지 않도록 기권 값을 계약에 둔다 */
  figure: Figure;
  /** 문항의 기호에 정한 값(엔진의 정규 이름) */
  values: { name: string; value: number }[];
  /** 값을 정하지 못해 null 로 둔 기호 */
  unset: string[];
  /** 읽지 못해 버린 것의 사유. 조용히 버리지 않고 판정에 싣는다 */
  issues: string[];
  steps: string[];
  quick: string[];
  /** 객관식이면 고른 선지, 단답형이면 빈 문자열 */
  choice: string;
  /** 풀이 글의 그림 계획이 고른 색칠도구 이름 */
  tools: string[];
  /** 답의 수 */
  value: number | null;
  /** 답의 원문(식이면 LaTeX). 사람에게 보이는 줄에 쓴다. 수 value 는 검증에만 쓴다 */
  valueText: string;
  /** 그림에서 잴 값. 답이 p+q, 10a 처럼 잰 값에서 유도되면 잰 값 자체(예: 넓이 27/4). 답이 곧 잴 값이면 답과 같다. 잰 값과 대조하는 것 */
  measureValue: number | null;
  /** 문항이 구하라는 양. 엔진이 직접 세워 잰다 */
  target: Target;
  /** 사람에게 보이는 답 한 줄(choice 와 value 를 합친 것) */
  answer: string;
}

const CHOICES = ['', '①', '②', '③', '④', '⑤'];

/** 값 옮기기 스키마. 문항이 쓴 기호가 값 선언의 키다: 키는 전부 필수, 값은 수 또는 null(정하지 못함), 그 밖의 키는 금지.
 * extra 는 더하는 기호 */
export function solveSchema(symbols: readonly string[], extra: readonly string[] = [], tools: readonly string[] = []): Record<string, unknown> {
  const keys = [...symbols, ...extra.filter((e) => !symbols.includes(e))];
  return {
    type: 'object',
    properties: {
      figure: { type: 'string', enum: ['있음', '없음'], description: '문항에 그릴 그림(좌표평면의 곡선, 점, 도형)이 있는가. 수열, 경우의 수, 식만 다루는 문항은 「없음」' },
      values: {
        type: 'object',
        description: '문항이 쓴 기호의 값. 기호마다 풀이 글이 정한 수 하나, 정하지 못했으면 null',
        properties: Object.fromEntries(keys.map((k) => [k, { anyOf: [{ type: 'number' }, { type: 'null' }] }])),
        required: keys,
        additionalProperties: false,
      },
      choice: { type: 'string', enum: CHOICES, description: '객관식이면 고른 선지, 단답형이면 빈 문자열' },
      value: { anyOf: [{ type: 'number' }, { type: 'string' }], description: '답. 수, 또는 풀이 글의 식(LaTeX. 분수, 근호, e, π 를 포함해도 된다). 코드가 수로 계산한다. 객관식이면 고른 선지의 값' },
      measure_value: { anyOf: [{ type: 'number' }, { type: 'string' }], description: '그림에서 잴 값. 답이 p+q, 10a, 분모와 분자의 합처럼 잰 값에서 유도되면 그림에서 실제로 재는 양(예: 넓이 27/4 이면 "\\frac{27}{4}"). 답이 곧 잴 값이면 답과 같은 값. 곡선과 축이나 두 곡선 사이의 넓이는 measure.area 로 못 재고 algebraric 에 정적분 식 \\int_{a}^{b} … dx 를 주어 얻는다(그 식이 풀이 글의 $…$ 에 있어야 고를 수 있다)' },
      target: TARGET_SCHEMA,
      tools: { type: 'array', description: '풀이 글의 「그림 계획」이 정한 색칠도구의 이름. 글에 없는 이름은 만들지 않는다. 없으면 빈 배열', items: { type: 'string', enum: [...tools] }, maxItems: 16 },
      relations: { type: 'array', description: '문항과 풀이가 말하는 그림의 관계. 끝에서 코드가 화면 좌표로 검사한다. on(a=점, b=곡선이나 선이나 원), meet(a=점, b, c=만나는 두 개체), tangent(a=접선, b=곡선이나 원, c=접점), parallel(a, b=두 선), perpendicular(a, b=두 선). 이름은 풀이의 그림 선언과 같은 이름. 없으면 빈 배열', maxItems: 12, items: { type: 'object', properties: { kind: { type: 'string', enum: ['on', 'meet', 'tangent', 'parallel', 'perpendicular'] }, a: { type: 'string' }, b: { type: 'string' }, c: { anyOf: [{ type: 'string' }, { type: 'null' }] } }, required: ['kind', 'a', 'b', 'c'], additionalProperties: false } },
    },
    required: ['figure', 'values', 'choice', 'value', 'measure_value', 'target', 'tools', 'relations'],
    additionalProperties: false,
  };
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** 사람에게 보이는 답 한 줄 */
export function answerText(choice: string, value: number | null, text = ''): string {
  const shown = text.trim() ? (/[\\^_{}]/.test(text) ? `$${text.trim()}$` : text.trim()) : value === null ? '' : String(value);
  return [choice, shown].filter(Boolean).join(' ');
}

/** 모델 출력 → Solved. 모양이 아닌 항목은 버리되 버린 사유를 issues 에 남긴다. written 은 풀이 글이다 */
export function readSolved(raw: Record<string, unknown>, known: readonly string[] = [], written = ''): Solved {
  const issues: string[] = [];
  const read = readValues(raw.values, known);
  issues.push(...read.issues);
  const choice = typeof raw.choice === 'string' && CHOICES.includes(raw.choice) ? raw.choice : '';
  const value = finite(raw.value) ? raw.value : typeof raw.value === 'string' ? num(unwrapMath(raw.value)) : null;
  const valueText = typeof raw.value === 'string' ? visibleNotation(unwrapMath(raw.value)).trim() : '';
  if (typeof raw.value === 'string' && raw.value.trim() && value === null) issues.push(`답 "${raw.value.slice(0, 40)}" 을 수로 계산하지 못했다`);
  const mv = raw.measure_value;
  const measureValue = finite(mv) ? mv : typeof mv === 'string' && mv.trim() ? num(unwrapMath(mv)) : value;
  if (typeof mv === 'string' && mv.trim() && measureValue === null) issues.push(`잴 값 "${mv.slice(0, 40)}" 을 수로 계산하지 못했다`);
  const figure: Figure = raw.figure === '없음' ? '없음' : '있음';
  const values = Object.entries(read.values).map(([name, v]) => ({ name, value: v }));
  const tools = Array.isArray(raw.tools) ? raw.tools.filter((t): t is string => typeof t === 'string') : [];
  const target = raw.target === undefined ? NO_TARGET : readTarget(raw.target);
  return { figure, values, unset: read.unset, issues, steps: written ? [written] : [], quick: [], tools, choice, value, valueText, measureValue, target, answer: answerText(choice, value, valueText) };
}

/** 문항이 이름을 붙인 점. 끝 관문의 「개체 포함」 기준(GeoBuildBench 의 object coverage). 추출은 segments.ts 의 pointNamesIn */
export function problemPointNames(problem: string): string[] {
  return pointNamesIn(problem);
}

const hasSymbol = (node: unknown, name: string): boolean => node === name || (Array.isArray(node) && node.some((n) => hasSymbol(n, name)));

/** x 와 y 를 함께 품은 등식인가(f(x)=…, y=…, 구간별 정의, 역함수 관계). 상수, 부등식, x=α 같은 조건은 그림 대상이 아니다 */
const drawsCurve = (latex: string): boolean => {
  const json = readCurveShape(latex).json;
  return Array.isArray(json) && json[0] === 'Equal' && hasSymbol(json, 'x') && hasSymbol(json, 'y');
};

/** 문항 글이 품은 그림 대상(x 와 y 의 등식, 이름 붙은 점). 그림이 필요한지는 모델의 자기 보고가 아니라 이것으로 정한다 */
export function problemFigure(problem: string): { curves: string[]; points: string[] } {
  return { curves: givenFormulas((t) => readExpressionShape(t).latex !== null, problem).filter(drawsCurve), points: problemPointNames(problem) };
}

/** 수능 답 형식. 선지가 있는 문항은 ①~⑤ 중 하나, 없는 문항(단답형)은 0~999 의 정수. 어긋나면 사유, 맞거나 대조할 답이 없으면 빈 문자열(답이 없으면 미검증이 맡는다).
 * choice 를 모르는 호출(MCP 는 고른 선지의 값만 받는다)은 선지 검사를 하지 않는다 */
export function answerFormatIssue(problem: string, answer: { choice?: string; value: number | null }): string {
  if (CHOICES.slice(1).some((c) => problem.includes(c))) return answer.choice === undefined || answer.choice ? '' : '선지가 있는 문항인데 고른 선지가 없음';
  if (answer.value === null) return '';
  return Number.isInteger(answer.value) && answer.value >= 0 && answer.value <= 999 ? '' : `단답형 답은 0~999 의 정수인데 ${answer.value}`;
}

export type Acceptance = '성립' | '불성립' | '미검증';

/** 끝 관문. 원문과 변형 모두 같은 판정: 답 형식 위반, 그림 실패, 표현하지 못한 요구(gaps), 문항의 점 누락, 답 불일치는 불성립이고 사유를 모두 적는다.
 * 잰 값이 없어 대조를 못 하면 미검증. 전부 통과면 성립 */
export function judge(input: { figure: Figure; failCount: number; missingPoints: string[]; verdict: 'ok' | 'mismatch' | 'none'; detail: string; gaps?: string[]; answerIssue?: string; answerMissing?: boolean; independent?: { verdict: 'ok' | 'mismatch' | 'none'; detail: string } }): { acceptance: Acceptance; reason: string } {
  if (input.figure === '없음') return input.answerIssue ? { acceptance: '불성립', reason: `답 형식: ${input.answerIssue}` } : { acceptance: '미검증', reason: '그림 없는 문항. 잰 값이 없어 답을 대조하지 못함' };
  const reasons: string[] = [];
  if (input.answerIssue) reasons.push(`답 형식: ${input.answerIssue}`);
  if (input.answerMissing) reasons.push('답이 없다(수로 읽히는 답도, 고른 선지도 없음)');
  if (input.failCount) reasons.push(`그림 실패 ${input.failCount}`);
  if (input.gaps?.length) reasons.push(`표현하지 못한 요구: ${input.gaps.join('; ')}`);
  if (input.missingPoints.length) reasons.push(`문항의 점 ${input.missingPoints.join(', ')} 이 서지 않음`);
  if (input.verdict === 'mismatch') reasons.push(input.detail);
  if (input.independent?.verdict === 'mismatch') reasons.push(input.independent.detail);
  if (reasons.length) return { acceptance: '불성립', reason: reasons.join(' / ') };
  // 성립은 호스트가 잰 값과 코드의 독립 재계산이 모두 정답을 받쳐야 한다. 재계산을 못 하면 확인하지 못한 것이라 미검증이다
  if (input.verdict === 'ok') return !input.independent || input.independent.verdict === 'ok' ? { acceptance: '성립', reason: input.detail + (input.independent ? ` / ${input.independent.detail}` : '') } : { acceptance: '미검증', reason: `${input.detail} / 독립 재계산을 못 해 확인하지 못함: ${input.independent.detail}` };
  return { acceptance: '미검증', reason: input.detail };
}

/** 제시된 식: 문항의 식 + 풀이 글의 식. 중복 없이 */
export function givenWith(problem: string, solved: Solved): string[] {
  const given = givenFormulas((t) => readExpressionShape(t).latex !== null, problem, ...solved.steps);
  // 「식=값」(예: \int_{-1}^{2}…dx=\dfrac{27}{4})은 등식 전체가 한 항목이라 식만 고를 수 없었다(실측 2026-10-21 killer2 run1). 우변이 수이면 좌변도 항목으로 둔다
  const out = [...given];
  for (const g of given) {
    const at = g.indexOf('=');
    if (at <= 0 || g.indexOf('=', at + 1) >= 0) continue;
    const left = g.slice(0, at).trim(); const right = g.slice(at + 1).trim();
    if (left && right && num(unwrapMath(right)) !== null && readExpressionShape(left).latex !== null && !out.includes(left)) out.push(left);
  }
  return out;
}

/** 도구 선택 JSON 에 풀이의 값을 합친다: 풀이 값이 바탕이고 모델이 같은 기호에 값을 주면 그것이 앞선다. 이름은 정규 이름으로 모으고, 모델이 준 선언 중 읽지 못한 것의 사유는 issues 로 돌려준다 */
export function withValues(raw: Record<string, unknown>, solved: Solved, known: readonly string[] = []): { raw: Record<string, unknown>; issues: string[] } {
  const own = readValues(raw.symbols, known);
  const merged = { ...Object.fromEntries(solved.values.map((v) => [v.name, v.value])), ...own.values };
  return { raw: { ...raw, symbols: Object.entries(merged).map(([name, value]) => ({ name, value })) }, issues: own.issues };
}
